import React, {
  memo,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
// Assuming useKeydown is in a shared hooks directory
// Adjust the path if your hook is located elsewhere
import { useKeydown } from '../../hooks/useKeydown';
import {
  ListRowsViewProps,
  ListRowsItemProps,
  ListRowsNavigationArrowProps,
} from './type'; // Adjust path as needed

// Assuming SvgArrowUp and SvgArrowDown are available
// Adjust paths if they are located elsewhere
import SvgArrowUp from '../Svgs/SvgArrowUp'; // Example path
import SvgArrowDown from '../Svgs/SvgArrowDown'; // Example path

let TRANSFORM_TIMEOUT_ROWS: NodeJS.Timeout | null = null;   

// A simple NavigationArrow component for ListRowsView (vertical)
// You might want to reuse/adapt your existing NavigationArrow component
const VerticalNavigationArrow: React.FC<ListRowsNavigationArrowProps> = ({
  type,
  icon,
  onClick,
  show,
  customStyle,
  className,
}) => {
  if (!show) return null;
  return (
    <button
      onClick={onClick}
      className={`list-rows-arrow list-rows-arrow-${type} ${className || ''}`}
      style={{
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2,
        ...(type === 'up' ? { top: '5px' } : { bottom: '5px' }),
        ...customStyle,
      }}
      aria-label={type === 'up' ? 'Scroll Up' : 'Scroll Down'}
    >
      {icon}
    </button>
  );
};

/**
 * ListRowsView component for displaying a grid of items in multiple rows.
 * Supports keyboard navigation (up, down, left, right) across items and rows.
 * Features vertical virtualization for rows.
 */
export const ListRowsView: React.FC<ListRowsViewProps> = memo(
  ({
    id,
    uniqueKeyPrefix = 'list-row-item-',
    data,
    itemsTotal,
    itemsPerRow,
    itemWidth,
    itemHeight,
    gap = 0,
    rowGap = 0,
    rowBuffer = 2,
    visibleRowsInViewport = 5,
    isActive,
    initialActiveRow = 0,
    initialActiveCol = 0,
    nativeControle = false,
    debounceTime = 200,
    itemDirection = 'ltr',
    withTransition = true,
    onActiveIndexChange,
    onItemMouseEnter,
    onItemActivate,
    onNavigateOutUp,
    onNavigateOutDown,
    onNavigateOutLeft,
    onNavigateOutRight,
    onBack,
    renderItem,
    arrowsConfig = { show: false },
    edgeScrollConfig = { enabled: false, interval: 700, startDelay: 1000 },
  }) => {
    const scrollViewRef = useRef<HTMLDivElement>(null);

    const [activeRow, setActiveRow] = useState(initialActiveRow);
    const [activeCol, setActiveCol] = useState(initialActiveCol);
    // startRow is the first row index that is actually rendered (for virtualization)
    const [startRow, setStartRow] = useState(0);

    const [showUpArrow, setShowUpArrow] = useState(false);
    const [showDownArrow, setShowDownArrow] = useState(false);

    const [isAutoScrolling, setIsAutoScrolling] = useState(false);
    const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const autoScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const totalRows = useMemo(
      () => Math.ceil(itemsTotal / itemsPerRow),
      [itemsTotal, itemsPerRow]
    );

    // Function to calculate 1D data index from row and col
    const getDataIndex = useCallback(
      (rIdx: number, cIdx: number): number => rIdx * itemsPerRow + cIdx,
      [itemsPerRow]
    );

    // Effect to initialize active indices and startRow
    useEffect(() => {
      let validInitialRow = Math.max(0, Math.min(initialActiveRow, totalRows - 1));
      if (totalRows === 0) validInitialRow = 0;
      
      const itemsInInitialRow = (validInitialRow === totalRows - 1 && itemsTotal % itemsPerRow !== 0)
        ? itemsTotal % itemsPerRow
        : itemsPerRow;
      
      let validInitialCol = Math.max(0, Math.min(initialActiveCol, itemsInInitialRow - 1));
      if (itemsInInitialRow === 0) validInitialCol = 0;


      setActiveRow(validInitialRow);
      setActiveCol(validInitialCol);

      // Adjust startRow to ensure the initial active row is visible
      const newStartRow = Math.max(
        0,
        Math.min(
          validInitialRow - Math.floor(visibleRowsInViewport / 2),
          totalRows - visibleRowsInViewport
        )
      );
      setStartRow(Math.max(0, newStartRow)); // Ensure startRow is not negative

    }, [id, initialActiveRow, initialActiveCol, itemsTotal, itemsPerRow, totalRows, visibleRowsInViewport]);


    // Update startRow based on activeRow for virtualization
    useEffect(() => {
      if (totalRows <= visibleRowsInViewport) {
        setStartRow(0);
        return;
      }

      // If activeRow is close to the top edge of rendered rows
      if (activeRow < startRow + 1 && startRow > 0) {
        setStartRow(Math.max(0, activeRow - 1));
      }
      // If activeRow is close to the bottom edge of rendered rows
      else if (activeRow > startRow + visibleRowsInViewport - 2 && startRow < totalRows - visibleRowsInViewport) {
         setStartRow(Math.min(totalRows - visibleRowsInViewport, activeRow - visibleRowsInViewport + 2));
      }
       // Ensure startRow is within valid bounds if totalRows is small
      else if (startRow > totalRows - visibleRowsInViewport && totalRows > visibleRowsInViewport) {
        setStartRow(totalRows - visibleRowsInViewport);
      } else if (startRow < 0) {
        setStartRow(0);
      }

    }, [activeRow, totalRows, visibleRowsInViewport, startRow]);


    const changeActiveItem = useCallback((newRow: number, newCol: number) => {
        if (totalRows === 0 || itemsTotal === 0) {
            setActiveRow(0);
            setActiveCol(0);
            onActiveIndexChange?.(0,0,0);
            return;
        }

        newRow = Math.max(0, Math.min(newRow, totalRows - 1));
        
        const itemsInTargetRow = (newRow === totalRows - 1 && itemsTotal % itemsPerRow !== 0)
            ? itemsTotal % itemsPerRow
            : itemsPerRow;
        
        newCol = Math.max(0, Math.min(newCol, itemsInTargetRow - 1));

        if (newRow !== activeRow || newCol !== activeCol) {
            setActiveRow(newRow);
            setActiveCol(newCol);
            const dataIndex = getDataIndex(newRow, newCol);
            onActiveIndexChange?.(newRow, newCol, dataIndex);
        }
    }, [totalRows, itemsTotal, itemsPerRow, activeRow, activeCol, getDataIndex, onActiveIndexChange]);


    // Navigation handlers
    const handleUp = useCallback(() => {
      if (activeRow > 0) {
        changeActiveItem(activeRow - 1, activeCol);
      } else {
        onNavigateOutUp?.();
      }
    }, [activeRow, activeCol, changeActiveItem, onNavigateOutUp]);

    const handleDown = useCallback(() => {
      if (activeRow < totalRows - 1) {
        changeActiveItem(activeRow + 1, activeCol);
      } else {
        onNavigateOutDown?.();
      }
    }, [activeRow, activeCol, totalRows, changeActiveItem, onNavigateOutDown]);

    const handleLeft = useCallback(() => {
      if (activeCol > 0) {
        changeActiveItem(activeRow, activeCol - 1);
      } else {
        onNavigateOutLeft?.(activeRow);
      }
    }, [activeRow, activeCol, changeActiveItem, onNavigateOutLeft]);

    const handleRight = useCallback(() => {
      const itemsInCurrentRow = (activeRow === totalRows - 1 && itemsTotal % itemsPerRow !== 0)
        ? itemsTotal % itemsPerRow
        : itemsPerRow;
      if (activeCol < itemsInCurrentRow - 1) {
        changeActiveItem(activeRow, activeCol + 1);
      } else {
        onNavigateOutRight?.(activeRow);
      }
    }, [activeRow, activeCol, totalRows, itemsTotal, itemsPerRow, changeActiveItem, onNavigateOutRight]);
    
    const pageUp = useCallback(() => {
        const newRow = Math.max(0, activeRow - visibleRowsInViewport);
        changeActiveItem(newRow, activeCol);
    }, [activeRow, activeCol, visibleRowsInViewport, changeActiveItem]);

    const pageDown = useCallback(() => {
        const newRow = Math.min(totalRows - 1, activeRow + visibleRowsInViewport);
        changeActiveItem(newRow, activeCol);
    }, [activeRow, activeCol, totalRows, visibleRowsInViewport, changeActiveItem]);


    const handleOk = useCallback(() => {
      const dataIndex = getDataIndex(activeRow, activeCol);
      if (dataIndex < itemsTotal) {
        onItemActivate?.(data[dataIndex], activeRow, activeCol, dataIndex);
      }
    }, [activeRow, activeCol, getDataIndex, itemsTotal, data, onItemActivate]);

    const handleBack = useCallback(() => {
      onBack?.();
    }, [onBack]);

    // Item styling
    const getItemStyle = useCallback(
      (rIdx: number, cIdx: number): React.CSSProperties => {
        // Position relative to the scroll view container
        // The scroll view itself will be translated for row virtualization
        const style: React.CSSProperties = {
          position: 'absolute',
          width: `${itemWidth}rem`,
          height: `${itemHeight}rem`,
          top: `${rIdx * (itemHeight + rowGap)}rem`,
        };
        if (itemDirection === 'rtl') {
          style.right = `${cIdx * (itemWidth + gap)}rem`;
        } else {
          style.left = `${cIdx * (itemWidth + gap)}rem`;
        }
        return style;
      },
      [itemWidth, itemHeight, rowGap, gap, itemDirection]
    );

    // Render items
    const renderVisibleItems = useCallback(() => {
      if (itemsTotal === 0) return null;

      const itemNodes: React.ReactNode[] = [];
      // Determine the range of rows to render based on startRow, visibleRowsInViewport, and rowBuffer
      const renderStartRow = Math.max(0, startRow - rowBuffer);
      const renderEndRow = Math.min(totalRows, startRow + visibleRowsInViewport + rowBuffer);

      for (let r = renderStartRow; r < renderEndRow; r++) {
        const itemsInCurrentRow = (r === totalRows - 1 && itemsTotal % itemsPerRow !== 0)
            ? itemsTotal % itemsPerRow
            : itemsPerRow;

        for (let c = 0; c < itemsInCurrentRow; c++) {
          const dataIndex = getDataIndex(r, c);
          if (dataIndex < itemsTotal) {
            const itemProps: ListRowsItemProps = {
              key: `${uniqueKeyPrefix}r${r}-c${c}`,
              rowIndex: r,
              colIndex: c,
              dataIndex,
              style: getItemStyle(r, c),
              isActive: r === activeRow && c === activeCol && isActive,
              item: data[dataIndex],
              onMouseEnter: () => {
                if (isActive) { // Only change active item on hover if the list itself is active
                    changeActiveItem(r,c);
                    onItemMouseEnter?.(r, c, dataIndex);
                }
              }
            };
            itemNodes.push(renderItem(itemProps));
          }
        }
      }
      return itemNodes;
    }, [
      itemsTotal,
      startRow,
      visibleRowsInViewport,
      rowBuffer,
      totalRows,
      itemsPerRow,
      getDataIndex,
      uniqueKeyPrefix,
      getItemStyle,
      activeRow,
      activeCol,
      isActive,
      data,
      renderItem,
      onItemMouseEnter,
      changeActiveItem
    ]);

    // ScrollView transform for vertical virtualization
    useEffect(() => {
      if (!scrollViewRef.current) return;

      const verticalOffset = startRow * (itemHeight + rowGap);
      const transformValue = `translate3d(0, -${verticalOffset}rem, 0)`;

      scrollViewRef.current.style.transition = withTransition
        ? 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)' // Smoother transition
        : 'none';
      scrollViewRef.current.style.transform = transformValue;

      // For handling external events if needed (like in original ListView)
      window.dispatchEvent(new Event('transformstart_rows'));
      if (TRANSFORM_TIMEOUT_ROWS) clearTimeout(TRANSFORM_TIMEOUT_ROWS);
      TRANSFORM_TIMEOUT_ROWS = setTimeout(() => {
        window.dispatchEvent(new Event('transformend_rows'));
      }, 350); // Match transition duration

    }, [startRow, itemHeight, rowGap, withTransition]);

    // Arrow visibility
    useEffect(() => {
      if (arrowsConfig.show) {
        setShowUpArrow(startRow > 0);
        setShowDownArrow(startRow < totalRows - visibleRowsInViewport && totalRows > visibleRowsInViewport);
      } else {
        setShowUpArrow(false);
        setShowDownArrow(false);
      }
    }, [startRow, totalRows, visibleRowsInViewport, arrowsConfig.show]);

    // Keyboard navigation setup
    const keydownOptions = useMemo(
      () => ({
        isActive: isActive && nativeControle,
        up: handleUp,
        down: handleDown,
        left: handleLeft,
        right: handleRight,
        pageup: pageUp, // Or map to channel_up if preferred
        pagedown: pageDown, // Or map to channel_down
        ok: handleOk,
        back: handleBack,
        debounce: debounceTime,
      }),
      [
        isActive,
        nativeControle,
        handleUp,
        handleDown,
        handleLeft,
        handleRight,
        pageUp,
        pageDown,
        handleOk,
        handleBack,
        debounceTime,
      ]
    );
    useKeydown(keydownOptions);

    // Parent container style (determines viewport height for rows)
    const parentStyle = useMemo(() => {
        const calculatedHeight = visibleRowsInViewport * itemHeight + (visibleRowsInViewport -1) * rowGap;
        return {
            height: `${calculatedHeight}rem`,
            width: `${itemsPerRow * itemWidth + Math.max(0, itemsPerRow - 1) * gap}rem`, // Full width for all items in a row
            position: 'relative', // For absolute positioning of arrows and scroll zones
            overflow: 'hidden', // Crucial for virtualization effect
        };
    }, [visibleRowsInViewport, itemHeight, rowGap, itemsPerRow, itemWidth, gap]);


    // Edge scroll logic (vertical for rows)
    const startVerticalAutoScroll = useCallback((direction: 'up' | 'down') => {
        if (isAutoScrolling || !edgeScrollConfig.enabled) return;
        setIsAutoScrolling(true);
        const scrollFn = direction === 'up' ? handleUp : handleDown;

        autoScrollTimeoutRef.current = setTimeout(() => {
            scrollFn();
            autoScrollIntervalRef.current = setInterval(() => {
                scrollFn();
            }, edgeScrollConfig.interval);
        }, edgeScrollConfig.startDelay);
    }, [isAutoScrolling, edgeScrollConfig, handleUp, handleDown]);

    const stopVerticalAutoScroll = useCallback(() => {
        if (autoScrollIntervalRef.current) clearInterval(autoScrollIntervalRef.current);
        if (autoScrollTimeoutRef.current) clearTimeout(autoScrollTimeoutRef.current);
        autoScrollIntervalRef.current = null;
        autoScrollTimeoutRef.current = null;
        setIsAutoScrolling(false);
    }, []);

    useEffect(() => { // Cleanup auto-scroll on unmount
        return () => {
            stopVerticalAutoScroll();
        };
    }, [stopVerticalAutoScroll]);

    const hoverZoneCommonStyle: React.CSSProperties = {
        position: 'absolute',
        left: 0,
        width: '100%',
        height: '50px', // Adjust as needed, make it a prop?
        zIndex: 1, // Below arrows
    };

    return (
      <div
        id={id}
        className="list-rows-view-parent"
        style={parentStyle as React.CSSProperties}
        role="grid"
        aria-rowcount={totalRows}
        aria-colcount={itemsPerRow}
      >
        {edgeScrollConfig.enabled && startRow > 0 && (
            <div
                className="edge-scroll-zone edge-scroll-zone-up"
                style={{ ...hoverZoneCommonStyle, top: 0 }}
                onMouseEnter={() => startVerticalAutoScroll('up')}
                onMouseLeave={stopVerticalAutoScroll}
            />
        )}
        {edgeScrollConfig.enabled && startRow < totalRows - visibleRowsInViewport && (
             <div
                className="edge-scroll-zone edge-scroll-zone-down"
                style={{ ...hoverZoneCommonStyle, bottom: 0 }}
                onMouseEnter={() => startVerticalAutoScroll('down')}
                onMouseLeave={stopVerticalAutoScroll}
            />
        )}

        <VerticalNavigationArrow
          type="up"
          icon={arrowsConfig.upIcon || <SvgArrowUp />}
          onClick={handleUp}
          show={showUpArrow}
          customStyle={arrowsConfig.style}
          className={arrowsConfig.className}
        />
        <VerticalNavigationArrow
          type="down"
          icon={arrowsConfig.downIcon || <SvgArrowDown />}
          onClick={handleDown}
          show={showDownArrow}
          customStyle={arrowsConfig.style}
          className={arrowsConfig.className}
        />

        <div
          ref={scrollViewRef}
          className={`list-rows-scroll-view ${itemDirection === 'rtl' ? 'rtl' : ''}`}
          style={{
            position: 'relative', // Items are positioned absolutely to this
            width: '100%',
            height: `${totalRows * (itemHeight + rowGap) - rowGap}rem`, // Full height of all content
          }}
        >
          {renderVisibleItems()}
        </div>
      </div>
    );
  }
);

// Default export for convenience, or named if you prefer
// export default ListRowsView;
